import React from "react";

import {
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import {
  getCustomFieldsTable,
  parseCustomFieldValue,
} from "../../../../components/common/CustomFields";

import {
  getFilePublicURL,
  getKeyFilename,
  getPhotoUrl,
} from "../../../../utils/fileUtils";
import {
  CarrierType,
  CustomFieldsDisplay,
  Unit,
} from "../../../../utils/constants";
import { getPPU, getUnitName } from "../../../../utils/loadTypeUtils";
import { trimDecimal } from "../../../../utils/utils";
import { calcTotalQuantity } from "../OutboundUtils";

const getDisplayValue = (value) => {
  if (value == null) return "—";
  const str = String(value).trim();
  return str ? str : "—";
};

const formatCityStateZip = ({ city, state, zipCode }) => {
  const cityState =
    city && state ? `${city}, ${state}` : city ? city : state ? state : "";
  return [cityState, zipCode].filter(Boolean).join(" ").trim();
};

const getShippedFromFacility = (record) => {
  return record?.reloadYard || window.appUser?.reloadYard || null;
};

export const HeaderBlock = () => {
  const logoImage = window.appUser?.reloadYard?.logoImage;
  const logoURL = logoImage ? getPhotoUrl(logoImage.key, 128) : null;

  return (
    <div style={{ width: "100%" }}>
      {logoURL && (
        <div style={{ textAlign: "center" }}>
          <img
            alt="Yard Logo"
            src={logoURL}
            style={{
              margin: "0 auto",
              width: "auto",
              maxWidth: "90%",
              height: "auto",
              minHeight: "5em",
              maxHeight: "5em",
            }}
          />
        </div>
      )}
      <div>
        <Divider />
      </div>
      <div style={{ textAlign: "center" }}>
        <h2>
          <strong> OUTBOUND MANIFEST </strong>
        </h2>
      </div>
    </div>
  );
};

export const LogisticsBlock = ({ record }) => {
  const facility = getShippedFromFacility(record);
  const facilityName = facility?.name;
  const facilityStreet = facility?.street || facility?.address?.street;
  const facilityCity = facility?.city || facility?.address?.city;
  const facilityState = facility?.state || facility?.address?.state;
  const facilityZipCode = facility?.zipCode || facility?.address?.zipCode;

  const consignee = record?.consignee || record?.shipTo || null;
  const consigneeName = consignee?.name;
  const consigneeStreet = consignee?.street;
  const consigneeCity = consignee?.city;
  const consigneeState = consignee?.state;
  const consigneeZipCode = consignee?.zipCode;
  const consigneeCountry = consignee?.country;

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <Table key="logistics" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ textAlign: "left", width: "50%", padding: 3 }}>
              Shipped From
            </TableCell>
            <TableCell style={{ textAlign: "left", width: "50%", padding: 3 }}>
              Consignee
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell style={{ textAlign: "left", padding: 3 }}>
              <div style={{ fontWeight: 600 }}>
                {getDisplayValue(facilityName)}
              </div>
              <div>{getDisplayValue(facilityStreet)}</div>
              <div>
                {getDisplayValue(
                  formatCityStateZip({
                    city: facilityCity,
                    state: facilityState,
                    zipCode: facilityZipCode,
                  }),
                )}
              </div>
            </TableCell>
            <TableCell style={{ textAlign: "left", padding: 3 }}>
              <div style={{ fontWeight: 600 }}>
                {getDisplayValue(consigneeName)}
              </div>
              <div>{getDisplayValue(consigneeStreet)}</div>
              <div>
                {getDisplayValue(
                  [
                    formatCityStateZip({
                      city: consigneeCity,
                      state: consigneeState,
                      zipCode: consigneeZipCode,
                    }),
                    consigneeCountry,
                  ]
                    .filter(Boolean)
                    .join(" ")
                    .trim(),
                )}
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export const InfoBlock = ({ record }) => {
  const outboundCustomFields = (
    record?.account?.outboundCustomFields || []
  ).filter((field) => field.display.includes(CustomFieldsDisplay.POS));

  return (
    <div style={{ width: "100%" }}>
      <Table key="InfoBlock1" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell
              style={{ textAlign: "center", width: "50%", padding: "3px" }}
            >
              Shipment Date
            </TableCell>
            <TableCell
              style={{ textAlign: "center", width: "50%", padding: "3px" }}
            >
              Ref. Number
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell style={{ textAlign: "center", padding: "3px" }}>
              {new Date(record.departureDateTime).toLocaleDateString()}
            </TableCell>
            <TableCell style={{ textAlign: "center", padding: "3px" }}>
              {record.pickupNumber}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Table key="InfoBlock2" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell
              style={{ textAlign: "center", width: "33%", padding: "3px" }}
            >
              PO number
            </TableCell>
            <TableCell
              style={{ textAlign: "center", width: "33%", padding: "3px" }}
            >
              Consignee PO Number
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell style={{ textAlign: "center", padding: "3px" }}>
              {record.poNumber}
            </TableCell>
            <TableCell style={{ textAlign: "center", padding: "3px" }}>
              {record.consigneePoNumber}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Table key="InfoBlock3" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell
              style={{ textAlign: "center", width: "33%", padding: "3px" }}
            >
              {record.carrierType === CarrierType.RAIL
                ? "Railcar Number"
                : "Trailer Number"}
            </TableCell>
            <TableCell
              style={{ textAlign: "center", width: "33%", padding: "3px" }}
            >
              Arrived
            </TableCell>
            <TableCell
              style={{ textAlign: "center", width: "33%", padding: "3px" }}
            >
              Released
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell style={{ textAlign: "center", padding: "3px" }}>
              {record.railCarNumber}
            </TableCell>
            <TableCell style={{ textAlign: "center", padding: "3px" }}>
              {record.arrivalDateTime
                ? new Date(record.arrivalDateTime).toLocaleString()
                : "N/A"}
            </TableCell>
            <TableCell style={{ textAlign: "center", padding: "3px" }}>
              {record.departureDateTime
                ? new Date(record.departureDateTime).toLocaleString()
                : "N/A"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {getCustomFieldsTable(outboundCustomFields, record.customFields)}
    </div>
  );
};

const LineItemsBlock = ({ record, posPickList }) => {
  const inventoryCustomFields = (
    record?.account?.inventoryCustomFields || []
  ).filter((field) => field.display.includes(CustomFieldsDisplay.POS));
  const showProductCodeColumn = posPickList.some((lineItem) =>
    lineItem.inventory.productCode?.trim(),
  );
  const showPCSColumn = posPickList.some(
    (lineItem) => lineItem.inventory.pcs != null,
  );
  const pcsColumnName = record.totalUOM === Unit.UNIT ? "PPU" : "Pieces";

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <h3>
        <strong> INVENTORY SHIPPED </strong>
      </h3>
      <Table key="outboundInventory" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            {showProductCodeColumn && (
              <TableCell
                style={{ textAlign: "left", minWidth: "25%", padding: "3px" }}
              >
                Product Code
              </TableCell>
            )}
            <TableCell
              style={{ textAlign: "left", minWidth: "25%", padding: "3px" }}
            >
              Product Name
            </TableCell>
            <TableCell
              style={{ textAlign: "right", minWidth: "25%", padding: "3px" }}
            >
              {getUnitName(record.totalUOM)}
            </TableCell>
            {showPCSColumn && (
              <TableCell
                style={{ textAlign: "right", minWidth: "25%", padding: "3px" }}
              >
                {pcsColumnName}
              </TableCell>
            )}
            {inventoryCustomFields.map((field, index) => (
              <TableCell
                key={"ct_" + index}
                style={{ textAlign: "center", padding: "3px" }}
              >
                {field.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {posPickList.map((lineItem) => (
            <TableRow key={lineItem.id}>
              {showProductCodeColumn && (
                <TableCell style={{ textAlign: "left", padding: "3px" }}>
                  {lineItem.inventory.productCode
                    ? lineItem.inventory.productCode
                    : "--"}
                </TableCell>
              )}
              <TableCell style={{ textAlign: "left", padding: "3px" }}>
                {lineItem.inventory.productName}
              </TableCell>
              <TableCell style={{ textAlign: "right", padding: "3px" }}>
                {lineItem.totalQuantityToPick}
              </TableCell>
              {showPCSColumn && (
                <TableCell style={{ textAlign: "right", padding: "3px" }}>
                  {lineItem.inventory.pcs != null
                    ? lineItem.inventory.pcs
                    : "--"}
                </TableCell>
              )}
              {inventoryCustomFields.map((field, index) => {
                const customFieldValue = lineItem.inventory.customFields?.find(
                  (cf) => cf.startsWith(field.name),
                );

                return (
                  <TableCell
                    key={"inv_cf_" + index}
                    style={{ textAlign: "center", padding: "3px" }}
                  >
                    {parseCustomFieldValue(customFieldValue)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const TotalsBlock = ({ record, posPickList }) => {
  const totalQuantity = calcTotalQuantity(record);

  // PCS Totals and Flags
  let totalPCS = 0;
  let allMissingPCS = true;
  let somePCSNull = false;

  posPickList.forEach((lineItem) => {
    // PCS Calculation (inventory.pcs here is getPPU(inventory) from posPickList)
    const linePpu = lineItem.inventory.pcs;
    if (linePpu == null || linePpu === "") {
      somePCSNull = true;
    } else {
      allMissingPCS = false;
      if (record.totalUOM === Unit.UNIT) {
        totalPCS += linePpu * lineItem.totalQuantityToPick;
      } else {
        totalPCS += linePpu;
      }
    }
  });

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <Table key="outboundTotals" style={{ width: "100%" }}>
        <TableBody>
          <TableRow key="totalUOM">
            <TableCell
              style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
            >
              Total {getUnitName(record.totalUOM)}
            </TableCell>
            <TableCell
              style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
            >
              {totalQuantity}
            </TableCell>
          </TableRow>
          {!allMissingPCS && (
            <TableRow key="totalPCS">
              <TableCell
                style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
              >
                Total Pieces
              </TableCell>
              <TableCell
                style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
              >
                {somePCSNull ? "N/A" : trimDecimal(totalPCS).toLocaleString()}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export const AttachmentsBlock = ({ record }) => {
  const attachments =
    record.attachments?.length > 0 ? record.attachments : null;
  const bol = record.bolFile;

  if (!attachments && !bol) return <></>;
  return (
    <div style={{ width: "100%" }}>
      <div>
        <Divider />
      </div>
      <Table key="outboundDocs" style={{ width: "100%" }}>
        <TableBody>
          {attachments?.map((row) => (
            <TableRow>
              <TableCell style={{ textAlign: "left" }}>
                {row.key && (
                  <a
                    href={getFilePublicURL(row.key)}
                    style={{
                      margin: "0 auto",
                      width: "auto",
                      maxWidth: "90%",
                    }}
                  >
                    {getKeyFilename(row.key)}
                  </a>
                )}
              </TableCell>
            </TableRow>
          ))}
          {bol && (
            <TableRow>
              <TableCell style={{ textAlign: "left" }}>
                {bol.key && (
                  <a
                    href={getFilePublicURL(bol.key)}
                    style={{
                      margin: "0 auto",
                      width: "auto",
                      maxWidth: "90%",
                    }}
                  >
                    {getKeyFilename(bol.key)}
                  </a>
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export const PhotosBlock = ({ record }) => {
  const rowSize = 3;
  const photosRows = [];
  const photos = record.photos?.items ?? [];
  for (var i = 0; i < photos.length; i += rowSize) {
    photosRows.push(photos.slice(i, i + rowSize));
  }

  return (
    <div style={{ width: "100%" }}>
      <div>
        <Divider />
      </div>
      <Table key="outboundPhotos" style={{ width: "100%" }}>
        <TableBody>
          {photosRows.map((row) => (
            <TableRow>
              <TableCell style={{ textAlign: "center" }}>
                {row[0]?.key && (
                  <img
                    alt="Outbound Load"
                    src={getPhotoUrl(row[0].key, 256)}
                    style={{
                      margin: "0 auto",
                      width: "auto",
                      maxWidth: "90%",
                      height: "auto",
                      borderRadius: 5,
                    }}
                  />
                )}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {row[1]?.key && (
                  <img
                    alt="Outbound Load"
                    src={getPhotoUrl(row[1].key, 256)}
                    style={{
                      margin: "0 auto",
                      width: "auto",
                      maxWidth: "90%",
                      height: "auto",
                      borderRadius: 5,
                    }}
                  />
                )}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {row[2]?.key && (
                  <img
                    alt="Outbound Load"
                    src={getPhotoUrl(row[2].key, 256)}
                    style={{
                      margin: "0 auto",
                      width: "auto",
                      maxWidth: "90%",
                      height: "auto",
                      borderRadius: 5,
                    }}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const SealsBlock = ({ record }) => {
  const showSeals = record?.account?.trackOutboundSeals;
  if (!showSeals) return null;

  const sealBarCodes = record.seals?.items?.map((seal) => seal.barcode) || [];

  const renderSeals = () => {
    if (sealBarCodes.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={3}>no seals were entered</TableCell>
        </TableRow>
      );
    }

    const rows = [];
    for (let i = 0; i < sealBarCodes.length; i += 3) {
      rows.push(
        <TableRow key={i}>
          <TableCell style={{ fontSize: "0.8rem" }}>
            {sealBarCodes[i] || ""}
          </TableCell>
          <TableCell style={{ fontSize: "0.8rem" }}>
            {sealBarCodes[i + 1] || ""}
          </TableCell>
          <TableCell style={{ fontSize: "0.8rem" }}>
            {sealBarCodes[i + 2] || ""}
          </TableCell>
        </TableRow>,
      );
    }
    return rows;
  };

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <h3>
        <strong>SEALS</strong>
      </h3>
      <Table id="inboundInventory" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderSeals()}</TableBody>
      </Table>
    </div>
  );
};

export const NotesBlock = ({ record }) => {
  const filteredNotes = record.notes?.filter((note) => note.public);
  if (!filteredNotes || filteredNotes?.length === 0) return null;

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <h3>
        <strong> NOTES </strong>
      </h3>
      <Table key="inboundInventory" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell
              style={{ textAlign: "left", minWidth: "60%", padding: "3px" }}
            >
              Note
            </TableCell>
            <TableCell
              style={{ textAlign: "left", minWidth: "20%", padding: "3px" }}
            >
              By
            </TableCell>
            <TableCell
              style={{ textAlign: "right", minWidth: "20%", padding: "3px" }}
            >
              Date
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredNotes.map((lineItem) => (
            <TableRow key={lineItem.id}>
              <TableCell style={{ textAlign: "left", padding: "3px" }}>
                {lineItem.text}
              </TableCell>
              <TableCell style={{ textAlign: "left", padding: "3px" }}>
                {lineItem.by}
              </TableCell>
              <TableCell style={{ textAlign: "right", padding: "3px" }}>
                {new Date(lineItem.date).toLocaleDateString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const OutboundPOSBody = ({ record, enclosePhotos, encloseDocs }) => {
  const posPickList = record.shipmentLineItems.items.map((lineItem) => {
    return {
      ...lineItem,
      inventory: {
        ...lineItem.inventory,
        pcs: getPPU(lineItem.inventory),
      },
    };
  });

  return (
    <Paper elevation={0} style={{ maxWidth: "900px", padding: "10px" }}>
      <HeaderBlock />
      <InfoBlock record={record} />
      <LogisticsBlock record={record} />
      <LineItemsBlock record={record} posPickList={posPickList} />
      <TotalsBlock record={record} posPickList={posPickList} />
      <SealsBlock record={record} />
      <NotesBlock record={record} />
      {encloseDocs && <AttachmentsBlock record={record} />}
      {enclosePhotos && <PhotosBlock record={record} />}
    </Paper>
  );
};
